// Convert JSON-patch operations (as produced by `monitorResource`) into a
// fhirpath-patch `Parameters` resource that carries semantic intent: numeric
// array indices become `where(...)` filters keyed by element identity where
// possible. Part of the fhirpath-patch delete boundary — see
// ./DELETE-WHEN-MEDPLUM-SHIPS-TRANSPORT-FHIRPATH.md.
//
// Each op is converted against a working copy that has the preceding ops
// applied, because both JSON patch and fhirpath patch apply sequentially —
// identity keys must reflect the state the op will actually execute in.

import { deepClone, type TypedValue } from '@medplum/core'
import type { Parameters, Resource, ResourceType } from '@medplum/fhirtypes'
import { applyPatch, type Operation } from 'rfc6902'
import {
  FhirPathPatchEncodeError,
  fhirPathPatchOperationsToParameters,
  resolveSchemaType,
} from '#src/fhirpath-patch/parameters-codec.ts'
import type { FhirPathPatchOperation } from '#src/fhirpath-patch/types.ts'
import type { JsonLike, JsonObject } from '#src/json.ts'
import type { PatchOperation } from '#src/monitorResource.ts'

// Convert JSON-patch ops to fhirpath-patch `Parameters`. `ops` should exclude
// the `test /meta/versionId` precondition (it is carried as a sidecar and
// re-prepended at submit-time conversion). Throws `FhirPathPatchEncodeError`
// when an op cannot be represented (unsupported op type, unencodable value) —
// callers fall back to plain JSON patch.
export function jsonPatchToFhirPathPatch(
  ops: readonly PatchOperation<ResourceType>[],
  resource: Resource,
  sidecars?: { baseResource?: Resource }
): Parameters {
  const working = deepClone(resource)
  const operations: FhirPathPatchOperation[] = []

  for (const op of ops) {
    if (op.op === 'test') {
      continue
    }
    operations.push(...convertOp(op, working))
    const [error] = applyPatch(working, [op as Operation])
    if (error) {
      throw new FhirPathPatchEncodeError(
        `JSON patch op ${op.op} at '${op.path}' does not apply to the base resource: ${error.message}`
      )
    }
  }

  return fhirPathPatchOperationsToParameters(operations, sidecars)
}

interface PointerWalk {
  // Container the final pointer segment indexes into.
  parent: unknown
  // FHIRPath of `parent`.
  parentPath: string
  // Dotted schema path of `parent` (no indices, no where() filters) —
  // resolvable against the loaded schema to type op values.
  parentSchemaPath: string
  // Final (unescaped) pointer segment.
  last: string
}

function convertOp(
  op: PatchOperation<ResourceType>,
  working: Resource
): FhirPathPatchOperation[] {
  if (op.op === 'copy' || op.op === 'move') {
    throw new FhirPathPatchEncodeError(
      `JSON patch op '${op.op}' is not supported for fhirpath-patch conversion`
    )
  }

  const walk = walkPointer(op.path, working)

  switch (op.op) {
    case 'add':
      return convertAdd(op, walk, working)
    case 'remove':
      return convertRemove(walk)
    case 'replace':
      return convertReplace(op, walk)
    case 'test':
      return []
    default:
      throw new FhirPathPatchEncodeError(
        `Unsupported JSON patch op '${op.op satisfies never}'`
      )
  }
}

function convertAdd(
  op: PatchOperation<ResourceType>,
  { parent, parentPath, parentSchemaPath, last }: PointerWalk,
  working: Resource
): FhirPathPatchOperation[] {
  if (Array.isArray(parent) && parent.length === 0) {
    // FHIR has no empty arrays, so the server holds no collection to insert
    // into (e.g. `telecom ??= []` then push). Add the field instead; fhirpath
    // `add` creates it.
    const field = walkPointer(
      op.path.slice(0, op.path.lastIndexOf('/')),
      working
    )
    return [
      {
        type: 'add',
        path: field.parentPath,
        name: field.last,
        value: schemaTypedValue(op.value, parentSchemaPath),
      },
    ]
  }
  if (Array.isArray(parent)) {
    // Positional insert into an existing collection.
    const index = last === '-' ? parent.length : Number(last)
    return [
      {
        type: 'insert',
        path: parentPath,
        index,
        value: schemaTypedValue(op.value, parentSchemaPath),
      },
    ]
  }
  const fieldSchemaPath = `${parentSchemaPath}.${last}`
  if (Array.isArray(op.value)) {
    // Adding a whole array field — decompose into per-item adds (fhirpath
    // `add` appends, creating the array on first use).
    return op.value.map((item) => ({
      type: 'add' as const,
      path: parentPath,
      name: last,
      value: schemaTypedValue(item, fieldSchemaPath),
    }))
  }
  return [
    {
      type: 'add',
      path: parentPath,
      name: last,
      value: schemaTypedValue(op.value, fieldSchemaPath),
    },
  ]
}

function convertRemove({
  parent,
  parentPath,
  last,
}: PointerWalk): FhirPathPatchOperation[] {
  if (Array.isArray(parent)) {
    const index = Number(last)
    return [{ type: 'delete', path: elementPath(parentPath, parent, index) }]
  }
  const existing = (parent as JsonObject | undefined)?.[last]
  const fieldPath = `${parentPath}.${last}`
  if (Array.isArray(existing) && existing.length > 1) {
    // fhirpath `delete` targets a single element — remove a whole multi-item
    // array field one element at a time, last-to-first so positional
    // fallbacks stay valid as elements disappear.
    return existing
      .map((_, i) => ({
        type: 'delete' as const,
        path: elementPath(fieldPath, existing, i),
      }))
      .reverse()
  }
  return [{ type: 'delete', path: fieldPath }]
}

function convertReplace(
  op: PatchOperation<ResourceType>,
  { parent, parentPath, parentSchemaPath, last }: PointerWalk
): FhirPathPatchOperation[] {
  if (Array.isArray(parent)) {
    const index = Number(last)
    return [
      {
        type: 'replace',
        path: elementPath(parentPath, parent, index),
        value: schemaTypedValue(op.value, parentSchemaPath),
      },
    ]
  }
  const existing = (parent as JsonObject | undefined)?.[last]
  const fieldPath = `${parentPath}.${last}`
  const fieldSchemaPath = `${parentSchemaPath}.${last}`
  if (Array.isArray(op.value) || Array.isArray(existing)) {
    // Whole-array replacement has no single fhirpath op. Naively decomposing
    // into delete-every-old + add-every-new churns the whole collection — and
    // because callers often replace an array by reassigning a rebuilt copy
    // (e.g. `meta.tag`, `identifier`), even a one-element change arrives here as
    // a full-array replace. That destroys and recreates unrelated siblings
    // (tags from other systems, etc.), bloats the review diff, and races
    // concurrent writers. So when both sides are arrays of identity-keyed
    // elements, emit only the DELTA: delete elements dropped from `existing`,
    // add elements new in `op.value`, leave unchanged elements untouched.
    const delta = arrayDelta(existing, op.value, {
      fieldPath,
      parentPath,
      fieldSchemaPath,
      name: last,
    })
    if (delta) {
      return delta
    }
    // Fallback (an element lacks a stable identity, so a delta can't be keyed
    // safely): full per-item delete of the old then add of the new.
    const deletes = Array.isArray(existing)
      ? existing
          .map((_, i) => ({
            type: 'delete' as const,
            path: elementPath(fieldPath, existing, i),
          }))
          .reverse()
      : [{ type: 'delete' as const, path: fieldPath }]
    const newItems = Array.isArray(op.value) ? op.value : [op.value]
    return [
      ...deletes,
      ...newItems.map((item) => ({
        type: 'add' as const,
        path: parentPath,
        name: last,
        value: schemaTypedValue(item, fieldSchemaPath),
      })),
    ]
  }
  return [
    {
      type: 'replace',
      path: fieldPath,
      value: schemaTypedValue(op.value, fieldSchemaPath),
    },
  ]
}

// Walk all but the final pointer segment against the working resource,
// building the FHIRPath of the parent container along the way.
function walkPointer(pointer: string, working: Resource): PointerWalk {
  const segments = parsePointer(pointer)
  if (segments.length === 0) {
    throw new FhirPathPatchEncodeError(
      'Whole-resource JSON patch ops cannot be converted to fhirpath-patch'
    )
  }

  let parent: unknown = working
  let parentPath: string = working.resourceType
  let parentSchemaPath: string = working.resourceType
  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i]
    if (Array.isArray(parent)) {
      const index = Number(segment)
      if (!Number.isInteger(index) || index < 0 || index >= parent.length) {
        throw new FhirPathPatchEncodeError(
          `JSON pointer '${pointer}' indexes outside the base resource`
        )
      }
      parentPath = elementPath(parentPath, parent, index)
      // Array indices don't change the element type — schema path unchanged.
      parent = parent[index]
    } else {
      parent = (parent as JsonObject | undefined)?.[segment]
      parentPath = `${parentPath}.${segment}`
      parentSchemaPath = `${parentSchemaPath}.${segment}`
    }
  }

  return {
    parent,
    parentPath,
    parentSchemaPath,
    last: segments.at(-1) as string,
  }
}

function parsePointer(pointer: string): string[] {
  if (pointer === '') {
    return []
  }
  return pointer
    .split('/')
    .slice(1)
    .map((segment) => segment.replaceAll('~1', '/').replaceAll('~0', '~'))
}

// FHIRPath for one array element: a `where(...)` filter keyed by element
// identity when that identity is unique among siblings, else a positional
// `[index]` fallback. Identity priority: `url` (extensions) → `reference`
// (Reference arrays) → `system`+`code` (codings) → `system`+`value`
// (identifiers). This is a pragmatic heuristic, not an exhaustive FHIR
// identity model — anything it can't key degrades to the same positional
// addressing JSON patch uses today, and the uniqueness check plus the
// round-trip tests guard against a filter matching the wrong sibling.
function elementPath(
  collectionPath: string,
  collection: JsonLike[],
  index: number
): string {
  const item = collection[index]
  const identity = identityFields(item)
  if (identity) {
    const matches = collection.filter((sibling) =>
      identityMatches(sibling, identity)
    )
    if (matches.length === 1) {
      const clause = Object.entries(identity)
        .map(([field, value]) => `${field}=${fhirPathLiteral(value)}`)
        .join(' and ')
      return `${collectionPath}.where(${clause})`
    }
  }
  return `${collectionPath}[${index}]`
}

// Emit only the add/delete delta between an old and new array value, keyed by
// element identity (system+code, url, reference, system+value). Returns null —
// signalling the caller to fall back to a full delete+add — when either side
// isn't an array or any element lacks a stable identity (so we can't safely
// tell which element changed). An identity present on both sides whose full
// content differs is re-emitted as delete-then-add so a deep change still
// applies; identical elements are skipped entirely.
function arrayDelta(
  existing: JsonLike | undefined,
  next: JsonLike | undefined,
  paths: {
    fieldPath: string
    parentPath: string
    fieldSchemaPath: string
    name: string
  }
): FhirPathPatchOperation[] | null {
  if (!Array.isArray(existing) || !Array.isArray(next)) {
    return null
  }
  const keyOf = (item: JsonLike | undefined): string | undefined => {
    const identity = identityFields(item)
    if (!identity) {
      return undefined
    }
    return Object.entries(identity)
      .map(([field, value]) => `${field}=${value}`)
      .join('&')
  }
  // Bail to the full-replace fallback if anything can't be keyed.
  const existingKeys = existing.map(keyOf)
  const nextKeys = next.map(keyOf)
  if (existingKeys.includes(undefined) || nextKeys.includes(undefined)) {
    return null
  }

  const nextByKey = new Map(
    next.map((item, i) => [nextKeys[i] as string, item])
  )
  const existingByKey = new Map(
    existing.map((item, i) => [existingKeys[i] as string, item])
  )

  // Delete elements that are gone, or whose content changed (re-added below).
  // Reverse so positional `[index]` fallbacks inside elementPath stay valid as
  // earlier elements are removed.
  const deletes: FhirPathPatchOperation[] = []
  existing.forEach((item, i) => {
    const key = existingKeys[i] as string
    const replacement = nextByKey.get(key)
    const gone = !nextByKey.has(key)
    const changed =
      replacement !== undefined &&
      JSON.stringify(replacement) !== JSON.stringify(item)
    if (gone || changed) {
      deletes.push({
        type: 'delete',
        path: elementPath(paths.fieldPath, existing, i),
      })
    }
  })
  deletes.reverse()

  // Add elements that are new, or whose content changed (deleted above).
  const adds: FhirPathPatchOperation[] = []
  next.forEach((item, i) => {
    const key = nextKeys[i] as string
    const prior = existingByKey.get(key)
    const isNew = !existingByKey.has(key)
    const changed =
      prior !== undefined && JSON.stringify(prior) !== JSON.stringify(item)
    if (isNew || changed) {
      adds.push({
        type: 'add',
        path: paths.parentPath,
        name: paths.name,
        value: schemaTypedValue(item, paths.fieldSchemaPath),
      })
    }
  })

  return [...deletes, ...adds]
}

function identityFields(
  item: JsonLike | undefined
): Record<string, string> | undefined {
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return undefined
  }
  const o = item as JsonObject
  if (typeof o.url === 'string') {
    return { url: o.url }
  }
  if (typeof o.reference === 'string') {
    return { reference: o.reference }
  }
  if (typeof o.system === 'string' && typeof o.code === 'string') {
    return { system: o.system, code: o.code }
  }
  if (typeof o.system === 'string' && typeof o.value === 'string') {
    return { system: o.system, value: o.value }
  }
  return undefined
}

function identityMatches(
  item: JsonLike | undefined,
  identity: Record<string, string>
): boolean {
  if (!item || typeof item !== 'object') {
    return false
  }
  const o = item as JsonObject
  return Object.entries(identity).every(([field, value]) => o[field] === value)
}

function fhirPathLiteral(value: string): string {
  return `'${value.replaceAll('\\', '\\\\').replaceAll("'", "\\'")}'`
}

// Type an op value from the loaded schema at its dotted property path — no
// shape heuristics. Whether the resulting datatype is encodable as a
// Parameters value[x] is enforced downstream by `encodeValuePart` (e.g.
// Extension and anonymous BackboneElement types throw there).
function schemaTypedValue(
  value: JsonLike | undefined,
  schemaPath: string
): TypedValue {
  if (value === null || value === undefined || Array.isArray(value)) {
    throw new FhirPathPatchEncodeError(
      `Value at '${schemaPath}' cannot be represented as a Parameters value[x]`
    )
  }
  const type = resolveSchemaType(schemaPath)
  if (!type) {
    throw new FhirPathPatchEncodeError(
      `No schema datatype found at '${schemaPath}'`
    )
  }
  return { type, value }
}
